/**
 * Products.jsx - Products / Items list page.
 *
 * Displays a filterable, sortable DataTable of items from Business Central.
 * Supports search, category/criticality filtering, multi-source toggle,
 * CSV export, and creating new items via a modal form.
 *
 * Route: /products
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Download, Search, DollarSign } from "lucide-react";
import { DataTable, Badge, Modal, Loading, useToast } from "../components";
import { getItems, createItem } from "../api/products";
import { useCompany } from "../company/CompanyContext";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const CRITICALITY_OPTIONS = ["", "A", "B", "C"];
const CATEGORY_OPTIONS = [
  "",
  "RAW",
  "FIN",
  "PACK",
  "MERCH",
  "MRO",
  "SERVICE",
];

const EMPTY_FORM = {
  item_no: "",
  description: "",
  base_uom: "",
  item_category_code: "",
  criticality: "B",
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function ProductsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedCompany } = useCompany();

  /* Data state */
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Filter state */
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [criticality, setCriticality] = useState("");
  const [multiSourceOnly, setMultiSourceOnly] = useState(false);

  /* Modal state */
  const [showNewModal, setShowNewModal] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);

  /* ---------------------------------------------------------------- */
  /* Data fetching                                                     */
  /* ---------------------------------------------------------------- */

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const companyFilter = selectedCompany?.id !== "all" ? selectedCompany?.name : undefined;
      const data = await getItems({ pageSize: 1000, company: companyFilter });
      /* DB API returns { total, rows } */
      setItems(data.rows || data || []);
    } catch (err) {
      setError(err.message || "Failed to load items");
      toast("Failed to load items", "bad");
    } finally {
      setLoading(false);
    }
  }, [toast, selectedCompany]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  /* ---------------------------------------------------------------- */
  /* Derived data                                                      */
  /* ---------------------------------------------------------------- */

  const filteredItems = useMemo(() => {
    let result = items;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (item) =>
          (item.number || "").toLowerCase().includes(q) ||
          (item.description || "").toLowerCase().includes(q)
      );
    }

    if (category) {
      result = result.filter((item) => item.itemCategoryCode === category);
    }

    if (criticality) {
      result = result.filter(
        (item) =>
          (item.criticality || "") === criticality
      );
    }

    if (multiSourceOnly) {
      result = result.filter(
        (item) => Number(item.sourceCount || 0) > 1
      );
    }

    return result;
  }, [items, search, category, criticality, multiSourceOnly]);

  /* ---------------------------------------------------------------- */
  /* Handlers                                                          */
  /* ---------------------------------------------------------------- */

  const handleExport = useCallback(() => {
    if (filteredItems.length === 0) {
      toast("No items to export", "warn");
      return;
    }

    const headers = [
      "Item No",
      "Description",
      "Category",
      "UOM",
      "Criticality",
      "Sources",
      "Best Landed Cost",
      "Best Source",
      "ERP Cost",
      "On Hand",
      "Reorder Point",
    ];

    const csvRows = [
      headers.join(","),
      ...filteredItems.map((item) =>
        [
          item.number || "",
          `"${(item.description || "").replace(/"/g, '""')}"`,
          item.itemCategoryCode || "",
          item.baseUnitOfMeasureCode || "",
          item.criticality || "",
          item.sourceCount || 0,
          item.bestLandedCost || "",
          item.bestSource || "",
          item.unitCost || "",
          item.inventory || 0,
          item.reorderPoint || "",
        ].join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `product-catalogue-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast(`Exported ${filteredItems.length} items`, "good");
  }, [filteredItems, toast]);

  const handleCreateItem = useCallback(async () => {
    if (!form.item_no.trim() || !form.description.trim()) {
      toast("Item number and description are required", "warn");
      return;
    }

    setSubmitting(true);
    try {
      await createItem({
        number: form.item_no.trim(),
        displayName: form.description.trim(),
        description: form.description.trim(),
        baseUnitOfMeasureCode: form.base_uom.trim(),
        itemCategoryCode: form.item_category_code.trim(),
      });

      toast(`Item ${form.item_no} created successfully`, "good");
      setShowNewModal(false);
      setForm({ ...EMPTY_FORM });
      loadItems();
    } catch (err) {
      toast(err.message || "Failed to create item", "bad");
    } finally {
      setSubmitting(false);
    }
  }, [form, toast, loadItems]);

  const handleFormChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  /* ---------------------------------------------------------------- */
  /* Table columns                                                     */
  /* ------------------------------------------------------------------ */

  const columns = useMemo(
    () => [
      { key: "number", label: "Item no." },
      { key: "description", label: "Description" },
      { key: "itemCategoryCode", label: "Category" },
      { key: "baseUnitOfMeasureCode", label: "UOM" },
      {
        key: "criticality",
        label: "Criticality",
        render: (_, row) => row.criticality || "\u2014",
      },
      {
        key: "sourceCount",
        label: "Sources",
        type: "badge",
        render: (_, row) => {
          const count = Number(row.sourceCount || 0);
          return (
            <Badge
              text={String(count)}
              variant={count > 1 ? "good" : count === 1 ? "warn" : "bad"}
            />
          );
        },
      },
      {
        key: "bestLandedCost",
        label: "Best landed cost",
        type: "money",
        render: (_, row) => row.bestLandedCost || "\u2014",
      },
      {
        key: "bestSource",
        label: "Best source",
        render: (_, row) => row.bestSource || "\u2014",
      },
      { key: "unitCost", label: "ERP cost", type: "money" },
      { key: "inventory", label: "On hand", type: "int" },
      { key: "reorderPoint", label: "Reorder point", type: "int" },
    ],
    []
  );

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ------------------------------------------------------------------ */

  return (
    <>
      <Loading show={loading} text="Loading items..." />

      {/* Error banner */}
      {error && !loading && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ color: "var(--bad)" }}>
            {error}
            <button
              className="btn tiny"
              style={{ marginLeft: 12 }}
              onClick={loadItems}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={styles.filterBar}>
        <div style={styles.searchWrap}>
          <Search
            size={14}
            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)" }}
          />
          <input
            type="text"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={styles.select}
        >
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.filter(Boolean).map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        <select
          value={criticality}
          onChange={(e) => setCriticality(e.target.value)}
          style={styles.select}
        >
          <option value="">All criticality</option>
          {CRITICALITY_OPTIONS.filter(Boolean).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={multiSourceOnly}
            onChange={(e) => setMultiSourceOnly(e.target.checked)}
          />
          <span>Multi-source only</span>
        </label>
      </div>

      {/* Actions row */}
      <div style={styles.actionsRow}>
        <span style={styles.countBadge}>
          {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""}
        </span>
        <div style={styles.actionsBtns}>
          <button
            className="btn primary"
            onClick={() => setShowNewModal(true)}
          >
            <Plus size={14} /> Add item
          </button>
          <button className="btn" onClick={() => toast("Price line feature coming soon", "default")}>
            <DollarSign size={14} /> Add price line
          </button>
          <button className="btn" onClick={handleExport}>
            <Download size={14} /> Export catalogue
          </button>
        </div>
      </div>

      {/* Data table */}
      <div className="card">
        <div className="card-body flush">
          <DataTable
            columns={columns}
            rows={filteredItems}
            onRow={(row) => navigate(`/products/${encodeURIComponent(row.number)}`)}
            emptyText="No items match your filters"
          />
        </div>
      </div>

      {/* New Item Modal */}
      {showNewModal && (
        <Modal
          title="New Item"
          onClose={() => {
            if (!submitting) {
              setShowNewModal(false);
              setForm({ ...EMPTY_FORM });
            }
          }}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  setShowNewModal(false);
                  setForm({ ...EMPTY_FORM });
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={handleCreateItem}
                disabled={submitting}
              >
                {submitting ? "Creating..." : "Create Item"}
              </button>
            </>
          }
        >
          <div style={styles.formGrid}>
            <label style={styles.formLabel}>
              Item number <span style={{ color: "var(--bad)" }}>*</span>
              <input
                type="text"
                value={form.item_no}
                onChange={(e) => handleFormChange("item_no", e.target.value)}
                placeholder="e.g. ITEM-0001"
                style={styles.formInput}
                required
              />
            </label>

            <label style={styles.formLabel}>
              Description <span style={{ color: "var(--bad)" }}>*</span>
              <input
                type="text"
                value={form.description}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
                placeholder="Item description"
                style={styles.formInput}
                required
              />
            </label>

            <label style={styles.formLabel}>
              Base UOM
              <input
                type="text"
                value={form.base_uom}
                onChange={(e) => handleFormChange("base_uom", e.target.value)}
                placeholder="e.g. PCS, BOX, KG"
                style={styles.formInput}
              />
            </label>

            <label style={styles.formLabel}>
              Item category code
              <select
                value={form.item_category_code}
                onChange={(e) =>
                  handleFormChange("item_category_code", e.target.value)
                }
                style={styles.formInput}
              >
                <option value="">Select category</option>
                {CATEGORY_OPTIONS.filter(Boolean).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.formLabel}>
              Criticality
              <select
                value={form.criticality}
                onChange={(e) =>
                  handleFormChange("criticality", e.target.value)
                }
                style={styles.formInput}
              >
                {CRITICALITY_OPTIONS.filter(Boolean).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </Modal>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  filterBar: {
    display: "flex",
    gap: 14,
    marginBottom: 16,
    flexWrap: "wrap",
    alignItems: "center",
    padding: "14px 16px",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
  },
  searchWrap: {
    position: "relative",
    flex: "1 1 240px",
    minWidth: 200,
  },
  searchInput: {
    width: "100%",
    padding: "9px 12px 9px 34px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    background: "var(--bg)",
    color: "var(--ink)",
    outline: "none",
  },
  select: {
    padding: "9px 12px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    background: "var(--panel)",
    color: "var(--ink)",
    cursor: "pointer",
    minWidth: 130,
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
    color: "var(--ink-2)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  actionsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    flexWrap: "wrap",
    gap: 8,
  },
  countBadge: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--ink-2)",
  },
  actionsBtns: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  formGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  formLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink-2)",
  },
  formInput: {
    padding: "7px 10px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    background: "var(--panel)",
    color: "var(--ink)",
    outline: "none",
  },
};
